import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Button,
  Typography,
} from "@mui/material";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { useTranslation } from "react-i18next";
import api from "../../services/api";

function normalizePayload(payload) {
  const body = payload?.data || payload || {};
  return body.data || body;
}

function arrayValue(value) {
  return Array.isArray(value) ? value : [];
}

function numberValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function recipeTitle(recipe, t) {
  return recipe?.recipe_name || recipe?.name || t("smart_kitchen.pantry_intelligence.unknown_recipe");
}

function productTitle(item, t) {
  return item?.inventory_item_name || item?.product_name || item?.name || t("smart_kitchen.pantry_intelligence.unknown_item");
}

function recipeCountLabel(count, t) {
  return count === 1
    ? t("smart_kitchen.pantry_intelligence.recipe_singular")
    : t("smart_kitchen.pantry_intelligence.recipe_plural");
}

function pantryItemCountLabel(count, t) {
  return count === 1
    ? t("smart_kitchen.pantry_intelligence.pantry_item_singular")
    : t("smart_kitchen.pantry_intelligence.pantry_item_plural");
}

function itemCountLabel(count, t) {
  return count === 1
    ? t("smart_kitchen.pantry_intelligence.item_singular")
    : t("smart_kitchen.pantry_intelligence.item_plural");
}

function warningLabel(warning, t) {
  const key = warning?.key || warning?.code || "warning";
  if (key === "missing_recipe_product_mapping") {
    return t("smart_kitchen.pantry_intelligence.warning_missing_mapping", {
      recipeId: warning?.params?.recipe_id || t("smart_kitchen.empty_value"),
    });
  }
  const translationKey = `smart_kitchen.pantry_intelligence.warning_${key}`;
  const translated = t(translationKey, { defaultValue: "" });
  return translated || warning?.message || key;
}

function OpportunityLabel({ opportunity }) {
  const { t } = useTranslation();
  const count = opportunity?.affected_recipe_count || opportunity?.shopping_reduction_items || opportunity?.alternative_recipe_count || opportunity?.missing_expiry_count || 0;
  const params = {
    item: opportunity?.inventory_item_name || "",
    count,
    days: opportunity?.days_until_expiry,
    itemLabel: itemCountLabel(count, t),
    pantryItemLabel: pantryItemCountLabel(count, t),
    recipeLabel: recipeCountLabel(count, t),
  };
  return (
    <ListItemText
      primary={t(`smart_kitchen.pantry_intelligence.opportunity.${opportunity?.type}`, params)}
      secondary={t(`smart_kitchen.pantry_intelligence.priority.${opportunity?.priority || "low"}`)}
    />
  );
}

function SummaryTile({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 900 }}>
        {value}
      </Typography>
    </Paper>
  );
}

export default function PantryInsightsDialog({ open, mealPlanId, onClose }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const loadInsights = useCallback(async () => {
    if (!mealPlanId) return;
    setLoading(true);
    setError("");
    try {
      const response = await api.smartKitchen.getMealPlanPantryIntelligence(mealPlanId);
      setData(normalizePayload(response));
    } catch (err) {
      setData(null);
      setError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t("smart_kitchen.pantry_intelligence.load_error")
      );
    } finally {
      setLoading(false);
    }
  }, [mealPlanId, t]);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setError("");
    loadInsights();
  }, [loadInsights, open]);

  const summary = useMemo(() => data?.summary || {}, [data]);
  const opportunities = arrayValue(data?.opportunities);
  const recipeInsights = arrayValue(data?.recipe_insights);
  const warnings = arrayValue(data?.warnings);
  const splitMeals = data?.split_meals || {};

  const hasSplitContribution = Boolean(splitMeals?.saved_split_options_included);
  const alternativeRecipeCount = numberValue(splitMeals.alternative_recipe_count);
  const summaryTiles = useMemo(() => [
    {
      label: t("smart_kitchen.pantry_intelligence.utilization_score"),
      value: t("smart_kitchen.pantry_intelligence.score_value", {
        score: numberValue(summary.pantry_utilization_score),
      }),
    },
    {
      label: t("smart_kitchen.pantry_intelligence.inventory_used"),
      value: t("smart_kitchen.pantry_intelligence.used_total", {
        used: numberValue(summary.used_inventory_items),
        total: numberValue(summary.total_inventory_items),
      }),
    },
    {
      label: t("smart_kitchen.pantry_intelligence.unused_expiring"),
      value: numberValue(summary.unused_expiring_count),
    },
    {
      label: t("smart_kitchen.pantry_intelligence.shopping_reduction"),
      value: numberValue(summary.shopping_reduction_items),
    },
  ], [summary, t]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" data-testid="pantry-insights-dialog">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <InsightsRoundedIcon color="primary" />
        {t("smart_kitchen.pantry_intelligence.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Alert severity="info">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t("smart_kitchen.pantry_intelligence.read_only")}
            </Typography>
            <Typography variant="body2">
              {t("smart_kitchen.pantry_intelligence.subtitle")}
            </Typography>
          </Alert>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          {!loading && data ? (
            <>
              <Grid container spacing={1.5}>
                {summaryTiles.map((tile) => (
                  <Grid item xs={12} sm={6} md={3} key={tile.label}>
                    <SummaryTile label={tile.label} value={tile.value} />
                  </Grid>
                ))}
              </Grid>

              {hasSplitContribution ? (
                <Alert severity="success" data-testid="pantry-insights-split-meals">
                  {t("smart_kitchen.pantry_intelligence.split_meals_included", {
                    count: alternativeRecipeCount,
                    recipeLabel: recipeCountLabel(alternativeRecipeCount, t),
                  })}
                </Alert>
              ) : null}

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                  {t("smart_kitchen.pantry_intelligence.opportunities")}
                </Typography>
                {opportunities.length ? (
                  <List dense disablePadding>
                    {opportunities.map((opportunity, index) => (
                      <ListItem
                        key={`${opportunity?.type || "opportunity"}-${index}`}
                        alignItems="flex-start"
                        sx={{ borderBottom: "1px solid", borderColor: "divider", px: 0 }}
                      >
                        <OpportunityLabel opportunity={opportunity} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("smart_kitchen.pantry_intelligence.no_opportunities")}
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                  {t("smart_kitchen.pantry_intelligence.recipe_insights")}
                </Typography>
                <Stack spacing={1.5}>
                  {recipeInsights.length ? recipeInsights.map((recipe) => (
                    <Paper key={`${recipe?.source || "recipe"}-${recipe?.recipe_id}-${recipe?.meal_plan_item_id || ""}`} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            {recipeTitle(recipe, t)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t("smart_kitchen.pantry_intelligence.recipe_meta", {
                              matched: numberValue(recipe?.pantry_match_count),
                              matchedLabel: pantryItemCountLabel(numberValue(recipe?.pantry_match_count), t),
                              missing: numberValue(recipe?.missing_ingredient_count),
                              missingLabel: itemCountLabel(numberValue(recipe?.missing_ingredient_count), t),
                            })}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {recipe?.uses_expiring_items ? (
                            <Chip size="small" color="warning" label={t("smart_kitchen.pantry_intelligence.uses_expiring")} />
                          ) : null}
                          {recipe?.source === "saved_split_option" ? (
                            <Chip size="small" color="success" variant="outlined" label={t("smart_kitchen.pantry_intelligence.split_meal")} />
                          ) : null}
                        </Stack>
                      </Stack>

                      {arrayValue(recipe?.matched_items).length ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>{t("smart_kitchen.pantry_intelligence.matched_items")}:</strong>{" "}
                          {arrayValue(recipe.matched_items).map((item) => productTitle(item, t)).join(", ")}
                        </Typography>
                      ) : null}

                      {arrayValue(recipe?.missing_items).length ? (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>{t("smart_kitchen.pantry_intelligence.missing_items")}:</strong>{" "}
                          {arrayValue(recipe.missing_items).map((item) => productTitle(item, t)).join(", ")}
                        </Typography>
                      ) : null}

                      {arrayValue(recipe?.suggested_alternatives).length ? (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                            {t("smart_kitchen.pantry_intelligence.suggested_alternatives")}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                            {arrayValue(recipe.suggested_alternatives).map((suggestion) => (
                              <Chip
                                key={suggestion?.recipe_id || suggestion?.recipe_name}
                                label={t("smart_kitchen.pantry_intelligence.suggestion_chip", {
                                  name: recipeTitle(suggestion, t),
                                  matched: numberValue(suggestion?.matched_pantry_count),
                                })}
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                            {t("smart_kitchen.pantry_intelligence.suggestions_read_only")}
                          </Typography>
                        </Box>
                      ) : null}
                    </Paper>
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("smart_kitchen.pantry_intelligence.no_recipe_insights")}
                    </Typography>
                  )}
                </Stack>
              </Box>

              {warnings.length ? (
                <Alert severity="warning">
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    {t("smart_kitchen.pantry_intelligence.warnings")}
                  </Typography>
                  <List dense disablePadding>
                    {warnings.map((warning, index) => (
                      <ListItem key={`${warning?.key || "warning"}-${index}`} disableGutters>
                        <ListItemText primary={warningLabel(warning, t)} />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              ) : null}
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("smart_kitchen.pantry_intelligence.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
